import React, { useContext, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    FormControl,
    FormHelperText,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import DatePicker, { DateObject } from "react-multi-date-picker";
import styled from "@emotion/styled";
import { DataContext } from "../../context/data-provider";
import axiosInstance from "../../services/axios-instance";
import { LOGS_URL, WEEKLY_LOGS_URL } from "../../services/endpoints";
import { CurrentWeekLogsInterface } from "../../interface/currentWeekLogsInterface";
import { Client } from "../../interface/clientInterface";
import * as Yup from "yup";
import { useTheme } from '@mui/material/styles';

const ModalWrapper = styled(Box)`
    padding: 1.5rem 1rem 1rem 1rem;
    width: fit-content;
    background-color: #fcfffd;
    border-radius: 8px;
`;

const RequestDaysForm = styled(Box)`
    width: 52ch;
    height: 3.4rem;
    border-radius: 4px;
    border: 1px solid #c0c0c0;
`;

const CustomTextField = styled(TextField)`
    background-color: #fcfffd;
    border-radius: 4px;

    textarea {
        min-height: 90px;
    }
`;

const RequestForm = styled(Box)`
    .rmdp-input {
        background-color: #fcfffd;
        text-align: center;
        height: 3.25rem;
        width: 29ch;
        font-size: 1rem;
        border-radius: 0 4px 4px 0;
        border-top: none;
        border-right: none;
        border-bottom: none;
        border-color: #c0c0c0;
        :hover {
            cursor: pointer;
            filter: brightness(95%);
        }
    }

    .rmdp-calendar {
        margin: 3px;
    }

    .rmdp-week-day {
        color: #3b3b3b;
    }

    .rmdp-day.rmdp-today > span {
        background-color: #b6d9fd;
        color: black;
    }

    .pickerinput {
        background-color: #fcfffd;
    }
`;

const validationSchema = Yup.object().shape({
    description: Yup.string().required("Description is required"),
    client_id: Yup.number().required("Client is required"),
    duration: Yup.string().test({
        name: 'customValidation',
        message: 'Duration is required',
        test: function() {
          const { hours, minutes } = this.parent;
          return hours !=0 || minutes != 0;
        }
      })
});

interface TrackerFormParams {
    onClose: () => void;
    setCurrentWeekLogs: React.Dispatch<
        React.SetStateAction<CurrentWeekLogsInterface[]>
    >;
    selectedWeek: number;
    selectedYear: number;
    openedDateTrackerForm: string;
    editTaskDetails: CurrentWeekLogsInterface | null;
    firstDayOfWeek: Date;
}

const TrackerForm: React.FC<TrackerFormParams> = ({
    onClose,
    setCurrentWeekLogs,
    openedDateTrackerForm,
    editTaskDetails,
    firstDayOfWeek,
}: TrackerFormParams) => {
    const { currentUser } = useContext(DataContext);
    const currentDate = new DateObject().format("YYYY-MM-DD");
    const [taskId, setTaskId] = useState<number | null>(null);
    const [editMode, setEditMode] = useState(false);
    const theme = useTheme();
    const [buttonDisabled, setButtonDisabled] = useState(false);

    const [date, setDate] = useState(
        editTaskDetails?.date || openedDateTrackerForm || currentDate
    );

    const [err, setErr] = useState<string>("");
    const [clients, setClients] = useState<Client[]>([]);

    const dateHandler = (event: DateObject | any) => {
        setDate(event);
    };

    useEffect(() => {
        if (editTaskDetails) {
            setEditMode(true);
            setTaskId(editTaskDetails.id);
        }
    }, [editTaskDetails]);

    useEffect(() => {
        const fetchClients = async () => {
            try {
                const response = await axiosInstance.get(
                    `/users/${currentUser.id}/clients`
                );
                const clientsData = response.data.map(
                    ({
                        pivot: { client_id },
                        name,
                    }: {
                        pivot: { client_id: number };
                        name: string;
                    }) => ({
                        id: client_id,
                        name,
                    })
                );

                setClients(clientsData);
            } catch (error) {
                console.error(error);
            }
        };
        fetchClients();
    }, []);

    useEffect(() => {
        const initializeForm = async () => {
            if (editTaskDetails && !editTaskDetails?.client_id) {
                try {
                    const clientResponse = await axiosInstance.get(
                        `/clients/${editTaskDetails?.client_id}`
                    );
                    const clientId = clientResponse.data.data;
                    editTaskDetails.client_id = clientId;
                } catch (error) {
                    console.error(error);
                }
            }
        };

        initializeForm();
    }, [editTaskDetails]);

    const handleSubmit = async (values: Partial<CurrentWeekLogsInterface>) => {
        setButtonDisabled(true);
        const { description, hours = 0, minutes = 0, client_id } = values;
        const duration = (hours*60) + minutes
        const timeLogObject = {
            description,
            user_id: currentUser.id,
            date: date.toString(),
            duration,
            client_id,
        };

        try {
            if (editMode && taskId !== null) {
                const response = await axiosInstance.put(
                    `${LOGS_URL}/${taskId}`,
                    timeLogObject
                );
                if (response.status === 200) {
                    onClose();
                    if (currentUser.id) {
                        const updatedResponse = await axiosInstance.get(
                            WEEKLY_LOGS_URL(
                                currentUser.id,
                                firstDayOfWeek.toString()
                            )
                        );
                        setCurrentWeekLogs(updatedResponse.data.data);
                    }
                }
            } else {
                const response = await axiosInstance.post(
                    LOGS_URL,
                    timeLogObject
                );

                if (response.status === 200 || response.status === 201) {
                    onClose();
                    if (currentUser.id) {
                        const updatedResponse = await axiosInstance.get(
                            WEEKLY_LOGS_URL(
                                currentUser.id,
                                firstDayOfWeek.toString()
                            )
                        );
                        setCurrentWeekLogs(updatedResponse.data.data);
                    }
                }
            }
        } catch (error) {
            setErr("Error occurred. Please try again.");
            console.error(error);
        }
        setButtonDisabled(false);
    };

    return (
        <Dialog
            sx={{
                "& .MuiDialog-paper": {
                    backgroundColor: "background.default",
                },
            }}
            open={true}
        >
            <Typography
                component="h1"
                variant="h6"
                sx={{
                    textAlign: "center",
                    mt: 3.5,
                }}
            >
                {editMode ? `Edit task` : "Fill in the Work Day Details"}
            </Typography>
            <ModalWrapper
                sx={{
                    [theme.breakpoints.down('sm')]: {
                        width: '100% !important',
                    },
                }}
            >
                <Formik
                    initialValues={{
                        description: editTaskDetails?.description || "",
                        duration: editTaskDetails?.duration || undefined,
                        hours: editTaskDetails?.duration ?  Math.floor(editTaskDetails?.duration/60) : 0,
                        minutes: editTaskDetails?.duration ?  (editTaskDetails?.duration%60) : 0,
                        client_id: editTaskDetails?.client_id || "",
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ values, touched, errors, handleChange }) => (
                        <Form>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                }}
                            >
                                <Box>
                                    <RequestDaysForm
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            [theme.breakpoints.down('sm')]: {
                                                width: '100% !important',
                                                justifyContent: "left",
                                            }
                                        }}
                                    >
                                        <p
                                            style={{
                                                paddingLeft: "12px",
                                                paddingRight: "12px",
                                            }}
                                        >
                                            Select the Date
                                        </p>
                                        <RequestForm
                                        className="datepicker_field"
                                         sx={{
                                            [theme.breakpoints.down('sm')]: {
                                                width: '100% !important',
                                            },
                                        }}
                                        >
                                            <DatePicker
                                                format="YYYY-MM-DD"
                                                value={date}
                                                onChange={(event) =>
                                                    dateHandler(event)
                                                }
                                            />
                                        </RequestForm>
                                    </RequestDaysForm>
                                </Box>
                                <Box>
                                    <RequestDaysForm
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            [theme.breakpoints.down('sm')]: {
                                                width: 'auto !important',
                                            },
                                        }}
                                    >
                                        <p
                                            style={{
                                                paddingLeft: "12px",
                                                paddingRight: "12px",
                                            }}
                                        >
                                            Duration
                                        </p>
                                        <RequestForm sx={{ width: "56%", display: "flex" }}>
                                            <OutlinedInput
                                                id="hours"
                                                sx={{
                                                    borderRadius: "0 4px 4px 0",
                                                }}
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        h
                                                    </InputAdornment>
                                                }
                                                value={values.hours}
                                                type="number"
                                                inputProps={{
                                                    min: 0,
                                                }}
                                                onChange={handleChange}
                                                error={
                                                    touched.hours &&
                                                    Boolean(errors.hours)
                                                }
                                            />
                                            <OutlinedInput
                                                id="minutes"
                                                sx={{
                                                    borderRadius: "0 4px 4px 0",
                                                }}
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        min
                                                    </InputAdornment>
                                                }
                                                value={values.minutes}
                                                type="number"
                                                inputProps={{
                                                    min: 0,
                                                }}
                                                onChange={handleChange}
                                                error={
                                                    touched.minutes &&
                                                    Boolean(errors.minutes)
                                                }
                                            />
                                                <OutlinedInput
                                                id="duration"
                                                sx={{
                                                    opacity: "0",
                                                    position: "absolute",
                                                    top: "0",
                                                    left: "0",
                                                }}
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        min
                                                    </InputAdornment>
                                                }
                                                value={(values.minutes ?? 0) + ((values.hours ?? 0) * 60)}
                                                type="number"
                                                onChange={handleChange}
                                                error={
                                                    touched.duration &&
                                                    Boolean(errors.duration)
                                                }
                                            />
                                        </RequestForm>
                                    </RequestDaysForm>
                                    {touched.duration && errors.duration && (
                                        <FormHelperText error sx={{ ml: 1.75 }}>
                                            {errors.duration}
                                        </FormHelperText>
                                    )}
                                </Box>
                            </Box>
                            <Box>
                                <FormControl sx={{ my: 2, minWidth: "100%" }}>
                                    <InputLabel id="client-label">
                                        Client
                                    </InputLabel>
                                    <Field
                                        name="client_id"
                                        as={Select}
                                        label="Client"
                                        value={values.client_id}
                                        onChange={handleChange}
                                        fullWidth
                                        error={
                                            touched.client_id &&
                                            Boolean(errors.client_id)
                                        }
                                    >
                                        {clients.map((client) => (
                                            <MenuItem
                                                key={client.id}
                                                value={client.id}
                                            >
                                                {client.name}
                                            </MenuItem>
                                        ))}
                                    </Field>
                                    {touched.client_id && errors.client_id && (
                                        <FormHelperText error sx={{ ml: 1.75 }}>
                                            {errors.client_id}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Box>
                            <Box
                                component="div"
                                sx={{
                                    "& > :not(style)": { width: "52ch" },
                                    mt: 2,
                                    mb: 2,
                                }}
                                // noValidate
                                //autoComplete="off"
                            >
                                <CustomTextField
                                   sx={{
                                    [theme.breakpoints.down('sm')]: {
                                        width: '100% !important',
                                    },
                                    }}
                                    id="request-description"
                                    multiline={true}
                                    name="description"
                                    label="Description*"
                                    variant="outlined"
                                    value={values.description}
                                    onChange={handleChange}
                                    error={
                                        touched.description &&
                                        Boolean(errors.description)
                                    }
                                    helperText={
                                        touched.description &&
                                        errors.description
                                    }
                                />
                            </Box>
                            {err !== "" && (
                                <Alert
                                    onClick={() => {
                                        setErr("");
                                    }}
                                    variant="filled"
                                    severity="error"
                                >
                                    {err}
                                </Alert>
                            )}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    mt: 3,
                                }}
                            >
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="error"
                                    disabled={buttonDisabled}
                                >
                                    Submit
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={onClose}
                                >
                                    Close
                                </Button>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </ModalWrapper>
        </Dialog>
    );
};

export default TrackerForm;
